import React from "react";
import { Alert, Typography } from "antd";

import { W0, W1 } from "@components/Constants";
import { CodeBlock } from "@components/Text";

import { type Specification } from "./helpers/getSpecification";

const DESCRIPTION = "This file is the Swagger 2.0 specification of the API of this key.";
const DESCRIPTION_IDENTIFIERS = "Give organizationId as a parameter of Authorize when the client does not call on the domain of the organization. Give sponsorId on operations that need the sponsor of your organization.";

const LABEL_URL = "Specification URL";
const LABEL_DOWNLOAD = "Download";
const LABEL_CLIENT_HOST = "Client Host";
const LABEL_ORGANIZATION_ID = "Organization ID (organizationId)";
const LABEL_SPONSOR_ID = "Sponsor ID (sponsorId)";

const LABEL_SANDBOX = "The API sandbox is the full documentation of this API";
const LINK_SANDBOX = "Open the API sandbox";
const DESCRIPTION_SANDBOX = "The sandbox gives each operation with its parameters and its responses, and it runs each operation against this environment.";

const LABEL_OPTION = "Option";
const LABEL_OPTION_AGENT = "Give the specification to a coding agent";
const DESCRIPTION_OPTION_AGENT = "Paste the URL into the agent, or add the file to the project. The agent can then write the calls that agree with this API.";

const LABEL_OPTION_TYPES = "Make the types of your client";
const DESCRIPTION_OPTION_TYPES = "Point a generator at the URL. Then set the base URL of the client to the host above.";

const FIRST_OPTION_NUMBER = 1;

type Option = {
  title: string;
  description: string;
  command?: string;
};

type Props = {
  sandboxUrl?: string;
  sponsorId?: string;
  organizationId?: string;
  specification: Specification;
};

const getTypesCommand = (url: string): string => {
  const command = `npx @hey-api/openapi-ts -i ${url} -o src/api`;

  return command;
};

const getOptions = (url: string): Option[] => {
  const typesCommand = getTypesCommand(url);

  const options = [
    {
      title: LABEL_OPTION_AGENT,
      description: DESCRIPTION_OPTION_AGENT,
    },
    {
      title: LABEL_OPTION_TYPES,
      description: DESCRIPTION_OPTION_TYPES,
      command: typesCommand,
    },
  ];

  return options;
};

const OrganizationPublicKeySpecification = ({
  sandboxUrl,
  sponsorId,
  organizationId,
  specification,
}: Props): JSX.Element => {
  const {
    url,
    host,
    path,
    title,
    fileName,
  } = specification;

  const options = getOptions(url);
  const urlLabel = `${LABEL_URL} (${title})`;

  const hasSandboxUrl = !!sandboxUrl;
  const hasSponsorId = !!sponsorId;
  const hasOrganizationId = !!organizationId;
  const hasIdentifiers = hasSponsorId || hasOrganizationId;

  const labelStyle = {
    marginBottom: W0 / 2,
  };

  const valueStyle = {
    marginBottom: W1,
    wordBreak: "break-all" as const,
  };

  const optionStyle = {
    marginBottom: W1,
  };

  const alertStyle = {
    marginBottom: W1,
  };

  const linkStyle = {
    marginTop: W0 / 2,
  };

  const titleStyle = {
    marginBottom: W0 / 2,
  };

  const renderValue = (label: string, value: string) => (
    <>
      <Typography.Paragraph type="secondary" style={labelStyle}>
        {label}
      </Typography.Paragraph>

      <Typography.Paragraph
        code
        copyable={{ text: value }}
        style={valueStyle}
      >
        {value}
      </Typography.Paragraph>
    </>
  );

  const renderOption = (
    { title: optionTitle, description, command }: Option,
    index: number,
  ) => {
    const optionNumber = index + FIRST_OPTION_NUMBER;
    const optionLabel = `${LABEL_OPTION} ${optionNumber}`;
    const headingText = `: ${optionTitle}`;
    const hasCommand = !!command;

    return (
      <div key={optionTitle} style={optionStyle}>
        <Typography.Paragraph style={titleStyle}>
          <Typography.Text strong>{optionLabel}</Typography.Text>

          {headingText}
        </Typography.Paragraph>

        <Typography.Paragraph type="secondary" style={titleStyle}>
          {description}
        </Typography.Paragraph>

        {hasCommand && (
          <CodeBlock isCopyable code={command} />
        )}
      </div>
    );
  };

  const sandboxDescription = (
    <>
      {DESCRIPTION_SANDBOX}

      <div style={linkStyle}>
        <Typography.Link
          href={sandboxUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {LINK_SANDBOX}
        </Typography.Link>
      </div>
    </>
  );

  return (
    <>
      {hasSandboxUrl && (
        <Alert
          showIcon
          type="info"
          message={LABEL_SANDBOX}
          description={sandboxDescription}
          style={alertStyle}
        />
      )}

      <Typography.Paragraph type="secondary">
        {DESCRIPTION}
      </Typography.Paragraph>

      {renderValue(LABEL_CLIENT_HOST, host)}

      {hasIdentifiers && (
        <>
          <Typography.Paragraph type="secondary">
            {DESCRIPTION_IDENTIFIERS}
          </Typography.Paragraph>

          {organizationId && renderValue(LABEL_ORGANIZATION_ID, organizationId)}
          {sponsorId && renderValue(LABEL_SPONSOR_ID, sponsorId)}
        </>
      )}

      <Typography.Paragraph type="secondary" style={labelStyle}>
        {urlLabel}
      </Typography.Paragraph>

      <Typography.Paragraph style={valueStyle}>
        <Typography.Text code copyable={{ text: url }}>
          {url}
        </Typography.Text>

        {" "}

        <Typography.Link
          href={path}
          download={fileName}
        >
          {LABEL_DOWNLOAD}
        </Typography.Link>
      </Typography.Paragraph>

      {options.map(renderOption)}
    </>
  );
};

export default OrganizationPublicKeySpecification;
